import type { PhotoProjection } from '@citizen-walk/shared-types';
import { MAX_PHOTO_URL_ATTEMPTS, usePhotoUrl } from './usePhotoUrl';

/**
 * 投稿サムネ表示 component (Phase 5 photo UI、thumbnail.jpg 経由)。
 *
 * Sprint AL (UI1 写真表示信頼性向上, docs/16 §10-2) で 5 状態に再構成:
 *   1. `path === null`              → 何も描画しない (null 返し)
 *   2. `usePhotoUrl` status='loading' → グレーの shimmer skeleton (`animate-pulse`)
 *      ※ 旧実装と異なり loading で 📷 を出さない (loading と final fallback を視覚分離)
 *   3. status='ready'               → 写真表示 (`<img>`)
 *   4. status='failed' (attempt < 3)→ 「⚠️ 読込失敗」+ 再試行 button
 *   5. status='failed' (attempt = 3)→ 固定 📷 アイコン + title 属性で失敗告知 (旧の見た目)
 *
 * **サイズ設計 (2026-06 改修)**: 旧実装は固定 60x60 (`w-[60px] h-[60px]`) だったため、
 * 一覧のカード表示で写真サイズ slider を上げても 60px のままだった (本 component を使う
 * カード側が高さ指定済の box に入れても無視されていた)。改修後は **親要素いっぱいに広がる**
 * 設計 (`w-full h-full`) にし、サイズは親 (一覧=64px box、カード=`photoSize` box) が決める。
 *   - `fit='cover'` (既定、一覧行): box を埋めてトリミング。
 *   - `fit='contain'` (カード): 投稿写真の全体が見えるよう letterbox 表示 (見切れ無し)。
 *
 * inline style は撤去し Tailwind class のみ使用 (CLAUDE.md design system 原則)。
 */
export interface PostThumbProps {
  /** Storage path (`districts/{id}/posts/{id}/thumbnail.jpg`)。 */
  path: string | null;
  /**
   * 投稿 doc に保存済の thumbnail download URL (docs/106、`Post.photoThumbUrl`)。
   * 渡すと usePhotoUrl が cache miss 時に楽観表示 + オフライン fallback に使う。
   */
  url?: string | null;
  /** alt 属性 (アクセシビリティ)。 */
  alt: string;
  /**
   * 画像の object-fit。
   *   - `'cover'` (既定): box を埋めてトリミング (一覧行の正方サムネ向け)。
   *   - `'contain'`: 投稿写真の全体を letterbox 表示 (カード表示で見切れさせない)。
   */
  fit?: 'cover' | 'contain';
  /** 写真の投影方式 (docs/27)。`equirectangular` なら「360°」バッジを重ねる。 */
  projection?: PhotoProjection | null;
}

/** 親要素を埋める共通 box: rounded + border + slate bg。📷 / ⚠️ などの状態表示に使う。 */
const BOX_CLASS = 'flex w-full h-full rounded border border-slate-300 bg-slate-200 overflow-hidden';

/** 中央寄せ文字 (📷 / ⚠️) を box 内に置くための inner class。 */
const CENTER_CLASS = 'items-center justify-center text-slate-500 text-xl';

export function PostThumb({ path, url, alt, fit = 'cover', projection = null }: PostThumbProps) {
  const photo = usePhotoUrl(path, url);
  if (!path) return null;

  // contain は letterbox 余白が目立たないよう薄い背景を敷く。
  const imgClass =
    fit === 'contain'
      ? 'block w-full h-full object-contain rounded border border-slate-300 bg-slate-50'
      : 'block w-full h-full object-cover rounded border border-slate-300';

  return (
    <span className="relative block w-full h-full">
      {projection === 'equirectangular' && (
        <span
          className="absolute left-1 top-1 z-10 rounded bg-black/60 px-1 text-[10px] font-bold text-white"
          aria-label="360度写真"
        >
          360°
        </span>
      )}
      {photo.status === 'ready' && photo.url ? (
        <img src={photo.url} alt={alt} loading="lazy" className={imgClass} />
      ) : photo.status === 'failed' && photo.attempt >= MAX_PHOTO_URL_ATTEMPTS ? (
        // 5. 固定 fallback (3 回失敗で確定)
        <span
          aria-label="写真の読込に失敗しました"
          title="写真の読込に失敗しました"
          className={`${BOX_CLASS} ${CENTER_CLASS}`}
        >
          📷
        </span>
      ) : photo.status === 'failed' ? (
        // 4. 再試行 UI (attempt < 3)
        <span
          role="group"
          aria-label="写真の読込に失敗しました"
          className={`${BOX_CLASS} flex-col items-center justify-center gap-0.5 p-0.5`}
        >
          <span className="text-[10px] leading-none text-slate-600" aria-hidden="true">
            ⚠️ 読込失敗
          </span>
          <button
            type="button"
            onClick={photo.retry}
            className="text-[10px] leading-none px-1 py-0.5 rounded border border-slate-400 bg-white text-slate-700 hover:bg-slate-50"
          >
            再試行
          </button>
        </span>
      ) : (
        // 2. loading skeleton (status==='loading' or 'idle' — path 有なら idle はほぼ無いが防御)
        <span
          aria-label="写真読込中"
          title="写真読込中"
          role="status"
          className={`${BOX_CLASS} animate-pulse`}
        />
      )}
    </span>
  );
}
